import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
import { eppoAssignmentCache, forgetServedFlag, recordEvaluation, resetServedAssignments, } from '#pplx/frontend/experimentation/assignmentStore.js';
import { getEppoAssignmentFlagKey, getEppoFlagSubjectType, } from '#pplx/frontend/experimentation/eppoSubjectTypes.js';
import { IndexedDBStore } from '#pplx/frontend/experimentation/IndexedDBStore.js';
import { EppoInitializationError } from '#pplx/frontend/experimentation/openfeature/eppoInitializationError.js';
import { loadEppoSdk } from '#pplx/frontend/experimentation/openfeature/loadEppoSdk.js';
import { logger as browserLogger } from '#pplx/frontend/logger/browser.js';
import { ErrorCode, StandardResolutionReasons } from '@openfeature/web-sdk';
const PERSISTENCE_STORE = new IndexedDBStore();
const DEFAULT_SUBJECT_TYPE = 'visitor_id';
function wipeDupeEppoKeys(prefix) {
    const keys = SafeLocalStorage.keys();
    const eppoKeys = keys.filter((key) => key.startsWith(prefix));
    if (eppoKeys.length > 1) {
        browserLogger.warn('[Eppo Experiments] Wiping duplicate Eppo keys', {
            keys: eppoKeys,
        });
        eppoKeys.forEach((key) => {
            SafeLocalStorage.removeItem(key);
        });
    }
}
async function loadEppoAssignmentClient({ apiKey, logAssignment, tenantSnapshot, }) {
    tenantSnapshot = tenantSnapshot || !BUILD_CAPABILITIES.remoteExperiments;
    if (!tenantSnapshot) {
        wipeDupeEppoKeys('eppo-configuration-');
        wipeDupeEppoKeys('eppo-configuration-meta-');
        wipeDupeEppoKeys('eppo-configuration-assignment-');
    }
    let eppoSdk;
    try {
        eppoSdk = await loadEppoSdk();
    }
    catch (error) {
        browserLogger.warn('[Eppo Experiments] SDK initialization failed', error);
        return undefined;
    }
    if (tenantSnapshot) {
        try {
            const response = await fetch('/api/tenant-flags', {
                credentials: 'same-origin',
                cache: 'no-store',
                redirect: 'error',
                signal: AbortSignal.timeout(5000),
            });
            if (!response.ok) {
                throw new Error('Tenant flag snapshot is unavailable.');
            }
            const snapshot = await response.json();
            if (snapshot.format !== 'SERVER' ||
                !snapshot.flags ||
                typeof snapshot.flags !== 'object' ||
                Array.isArray(snapshot.flags)) {
                throw new Error('Invalid tenant flag snapshot.');
            }
            // Use the SDK's memory-only evaluator; never initialize its CDN client or
            // reuse flags cached by a managed Perplexity deployment.
            const client = eppoSdk.offlineInit({
                flagsConfiguration: snapshot.flags,
                isObfuscated: false,
                throwOnFailedInitialization: true,
                assignmentLogger: { logAssignment },
            });
            client.useCustomAssignmentCache(eppoAssignmentCache);
            return client;
        }
        catch (error) {
            browserLogger.warn('[Eppo Experiments] Tenant snapshot initialization failed', error);
            return undefined;
        }
    }
    let cachedEntries = {};
    try {
        // This pre-read is load-bearing: HybridConfigurationStore.init() hydrates the serving
        // store before starting init's Promise.race. Returning this memoized snapshot makes the
        // warm-cache racer deterministic and lets it win in microtasks without re-hitting IDB.
        cachedEntries = (await PERSISTENCE_STORE.entries()) ?? {};
    }
    catch (error) {
        browserLogger.debug('[Eppo Experiments] IndexedDB pre-read failed', error);
    }
    const hasCache = Object.keys(cachedEntries).length > 0;
    // IndexedDBStore is a class, so prototype delegation preserves its methods unlike a spread.
    // Force expiration so the SDK tries the CDN while preserving setEntries for successful
    // responses to update both the serving store and IndexedDB.
    const persistentStore = Object.create(PERSISTENCE_STORE);
    persistentStore.entries = async () => cachedEntries;
    persistentStore.isExpired = async () => true;
    try {
        const client = await eppoSdk.init({
            apiKey,
            assignmentLogger: { logAssignment },
            persistentStore,
            // One retry means exactly two serial CDN attempts. The SDK waits a jittered ~1.5-3s
            // between them, so one init call is sufficient.
            numInitialRequestRetries: 1,
            // Essential for the hard requirement: a non-empty snapshot must remain a valid init
            // source despite isExpired() being forced true, rather than resolving on defaults.
            useExpiredCache: true,
            // A late successful fetch may transition cache to fresh in-session. Fresh to stale is
            // impossible here because setEntries runs only after fetch success, never on failure.
            updateOnFetch: 'always',
            // A populated cache completes initialization; only throw when neither cache nor CDN can
            // provide configuration.
            throwOnFailedInitialization: !hasCache,
        });
        // Our store replaces the SDK's own persisted dedup cache so every served experiment arm is
        // recorded (see assignmentStore.ts). The SDK's abandoned `eppo-assignment-*` cache means
        // returning browsers re-log each current arm once; downstream readers key on first exposure.
        client.useCustomAssignmentCache(eppoAssignmentCache);
        browserLogger.info('[Eppo Experiments] Primary key initialization successful');
        return client;
    }
    catch (error) {
        browserLogger.debug('[Eppo Experiments] Eppo SDK initialization failed', error);
        return undefined;
    }
}
export class EppoOpenFeatureProvider {
    metadata = { name: 'eppo-openfeature-provider' };
    runsOn = 'client';
    eppoClient;
    eppoClientPromise;
    options;
    constructor(options) {
        if ('assignmentClient' in options) {
            this.eppoClient = options.assignmentClient;
            return;
        }
        this.options = options;
    }
    async initialize() {
        const eppoClient = await this.getAssignmentClient();
        if (!eppoClient) {
            throw new EppoInitializationError();
        }
    }
    onContextChange() {
        // Identity changed without a reload: arms served to the previous identity no longer
        // describe this browser's requests until the SDK re-evaluates for the new one.
        resetServedAssignments();
        // OpenFeature marks a reconciled context READY even while initialize() is pending.
        // Join the same initialization so a fallback cannot be persisted as an assignment.
        if (!this.isReady())
            return this.initialize();
    }
    isReady() {
        return this.eppoClient !== undefined;
    }
    resolveValue(flagKey, defaultValue, context) {
        if (typeof defaultValue === 'boolean') {
            return this.resolveBooleanEvaluation(flagKey, defaultValue, context, browserLogger)
                .value;
        }
        if (typeof defaultValue === 'string') {
            return this.resolveStringEvaluation(flagKey, defaultValue, context, browserLogger).value;
        }
        if (typeof defaultValue === 'number') {
            return this.resolveNumberEvaluation(flagKey, defaultValue, context, browserLogger).value;
        }
        return this.resolveObjectEvaluation(flagKey, defaultValue, context, browserLogger).value;
    }
    resolveBooleanEvaluation(flagKey, defaultValue, context, logger) {
        return this.resolveAssignment(flagKey, defaultValue, context, logger, 'getBooleanAssignmentDetails');
    }
    resolveStringEvaluation(flagKey, defaultValue, context, logger) {
        return this.resolveAssignment(flagKey, defaultValue, context, logger, 'getStringAssignmentDetails');
    }
    resolveNumberEvaluation(flagKey, defaultValue, context, logger) {
        return this.resolveAssignment(flagKey, defaultValue, context, logger, 'getNumericAssignmentDetails');
    }
    resolveObjectEvaluation(flagKey, defaultValue, context, logger) {
        if (typeof defaultValue !== 'object' || defaultValue === null) {
            return {
                value: defaultValue,
                reason: StandardResolutionReasons.ERROR,
                errorCode: ErrorCode.TYPE_MISMATCH,
                errorMessage: 'Eppo JSON assignments require an object default value.',
            };
        }
        return this.resolveAssignment(flagKey, defaultValue, context, logger, 'getJSONAssignmentDetails');
    }
    resolveAssignment(flagKey, defaultValue, context, logger, method) {
        const eppoClient = this.eppoClient;
        if (!eppoClient) {
            return {
                value: defaultValue,
                reason: StandardResolutionReasons.ERROR,
                errorCode: ErrorCode.GENERAL,
                errorMessage: 'Eppo client is not initialized.',
            };
        }
        const eppoFlagKey = getEppoAssignmentFlagKey(flagKey);
        const subjectType = getSubjectType(flagKey);
        const subjectKey = getSubjectKey(subjectType, context);
        if (!subjectKey) {
            forgetServedFlag(eppoFlagKey);
            return {
                value: defaultValue,
                reason: StandardResolutionReasons.ERROR,
                errorCode: ErrorCode.TARGETING_KEY_MISSING,
                errorMessage: `OpenFeature evaluation context is missing ${subjectType} subject key.`,
            };
        }
        try {
            const subjectAttributes = {
                ...toEppoSubjectAttributes(context),
                subjectType,
            };
            const details = eppoClient[method](eppoFlagKey, subjectKey, subjectAttributes, defaultValue);
            recordEvaluation({
                flagKey: eppoFlagKey,
                subjectKey,
                allocationKey: details.evaluationDetails.matchedAllocation?.key ?? null,
                variationKey: details.evaluationDetails.variationKey,
            });
            return {
                value: details.variation,
                reason: StandardResolutionReasons.TARGETING_MATCH,
            };
        }
        catch (error) {
            logger.debug('[EppoOpenFeatureProvider] Assignment failed', {
                flag: eppoFlagKey,
                method,
                error,
            });
            return {
                value: defaultValue,
                reason: StandardResolutionReasons.ERROR,
                errorCode: ErrorCode.GENERAL,
                errorMessage: error instanceof Error ? error.message : String(error),
            };
        }
    }
    async getAssignmentClient() {
        if (this.eppoClient) {
            return this.eppoClient;
        }
        if (!this.options) {
            return undefined;
        }
        if (!this.eppoClientPromise) {
            this.eppoClientPromise = loadEppoAssignmentClient(this.options);
        }
        this.eppoClient = await this.eppoClientPromise;
        return this.eppoClient;
    }
}
function toEppoSubjectAttributes(context) {
    const { targetingKey: _targetingKey, ...subjectAttributes } = context;
    return subjectAttributes;
}
function getSubjectType(flagKey) {
    return getEppoFlagSubjectType(flagKey) ?? DEFAULT_SUBJECT_TYPE;
}
function getSubjectKey(subjectType, context) {
    switch (subjectType) {
        case 'user_nextauth_id':
            return (getStringContextValue(context, 'userId') ??
                getStringContextValue(context, 'user_nextauth_id'));
        case 'visitor_id':
            return (getStringContextValue(context, 'visitorId') ?? getStringContextValue(context, 'visitor_id'));
        case 'session_id':
            return (getStringContextValue(context, 'sessionId') ?? getStringContextValue(context, 'session_id'));
        case 'comet_device_id':
            return (getStringContextValue(context, 'cometDeviceId') ??
                getStringContextValue(context, 'comet_device_id'));
    }
}
function getStringContextValue(context, key) {
    const value = context[key];
    return typeof value === 'string' && value ? value : undefined;
}
//# sourceMappingURL=EppoOpenFeatureProvider.js.map